/**
 * The screen a clinician is on while a document is being printed (PHASE-0.md §2.6).
 *
 * A thin shell: it fetches, it shows what will come out of the printer, and it has the two
 * controls — print, and back. Everything that decides what the paper says is in
 * `AdministrationRecord.tsx`, `Wristband.tsx` and `printDisplay.ts`.
 *
 * **Everything on this screen except the document itself disappears when printing.** That
 * is `@media print` in `index.css`, and it is how §2.6's *looks like a hospital document,
 * not like a web page* is met: the banner, the app bar, the footer, these buttons and this
 * preview notice are all screen furniture. What is left on the page is a document that
 * carries its own heading, its own provenance and its own footer.
 *
 * **Opening this screen is the audited event, not pressing Print.** The fetch writes an
 * `audit.event` row with `action = 'print'` and the document name, from the route and before
 * the payload is serialised (`PrintEndpoints.cs`, `AGENTS.md` §4). So the log records that
 * the record was rendered for paper, which is the disclosure that matters — the browser's
 * print dialog is not observable from here, and a row written on a `window.print()` that a
 * clinician then cancelled would be a row that claims more than happened. One visit is one
 * row: there is no refresh timer and no refetch, for the reason `fetchPatientOverview` gives.
 *
 * The audit consequence of that is worth being explicit about, because it is the reason the
 * print buttons are on the patient screen rather than being a link somebody can land on:
 * a `print` row is written by arriving here, and the log is append-only.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { PatientOverview } from '../patient/patientOverviewApi'
import { AdministrationRecord } from './AdministrationRecord'
import {
  fetchPrintDocument,
  PrintError,
  type PrintDocument,
  type PrintFailure,
} from './printApi'
import { RecordExtract } from './RecordExtract'
import { Wristband } from './Wristband'

type State =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded'
      readonly overview: PatientOverview

      /**
       * When the document was produced.
       *
       * Taken once, when the payload arrived, and passed down rather than read in the
       * component. The footer's timestamp then names the moment the record was read for
       * paper — which is the same moment the audit row was written — and does not drift
       * forward on a re-render.
       */
      readonly loadedAt: Date
    }
  | { readonly kind: 'failed'; readonly failure: PrintFailure }

export function PrintScreen({
  patientId,
  documentKind,
  onBack,
}: {
  readonly patientId: string

  /**
   * Which of the two documents.
   *
   * Named `documentKind` rather than `document` on purpose: a prop called `document` would
   * shadow the global of that name inside a component whose whole job ends in a call to
   * `window.print()`.
   */
  readonly documentKind: PrintDocument

  readonly onBack: () => void
}) {
  // `common` first, for the reason PatientOverview.tsx gives: it is what lets this screen's
  // `t` reach the shared display functions. `print` last and additive.
  const { t } = useTranslation(['common', 'patient', 'print', 'death'])
  const [state, setState] = useState<State>({ kind: 'loading' })

  // A retry is a new attempt at the same document. It is also a second audit row, which is
  // correct — a failed render that somebody retried is two requests for the record.
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => {
    setState({ kind: 'loading' })
    setAttempt((value) => value + 1)
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    fetchPrintDocument(patientId, documentKind, controller.signal).then(
      (overview) => {
        if (!controller.signal.aborted) {
          setState({ kind: 'loaded', overview, loadedAt: new Date() })
        }
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return
        }
        setState({
          kind: 'failed',
          failure: cause instanceof PrintError ? cause.failure : 'unavailable',
        })
      },
    )

    return () => controller.abort()
  }, [patientId, documentKind, attempt])

  return (
    <main className="print-screen">
      {/*
        Not printed. `aria-hidden` is deliberately absent — this is the working part of the
        screen for anybody using it on screen, and only the paper has no use for it.
      */}
      <div className="print-screen__actions">
        <button type="button" className="print-screen__back" onClick={onBack}>
          {t('print:action.back')}
        </button>

        {state.kind === 'loaded' && (
          <button
            type="button"
            className="print-screen__print"
            // The browser's own dialog, which is what §2.6 asks for: no PDF toolchain on
            // ARM in phase 0. The clinician picks the printer their ward has.
            onClick={() => window.print()}
          >
            {t('print:action.print')}
          </button>
        )}

        <p className="print-screen__preview">{t('print:document.preview')}</p>

        {/*
          Screen only, and deliberately advisory rather than a block.

          A patient with skyddad identitet still needs a wristband and a signeringslista —
          withholding them would mean the ward identifies that patient worse than everybody
          else, which is the opposite of what the marking is for. So the document prints in
          full and the clinician is told to think before the paper leaves their hands. What
          a printed record for a sekretessmarkerad patient should say, and whether it should
          be routed to a particular printer, is a question for clinical review: it is parked
          in `docs/STATUS.md` rather than guessed at here.
        */}
        {state.kind === 'loaded' && state.overview.confidentialityMarked && (
          <p className="print-screen__confidentiality" role="note">
            {t('print:document.confidentialityReview')}
          </p>
        )}
      </div>

      <div aria-live="polite">
        {state.kind === 'loading' && (
          <p className="print-screen__notice">{t('print:document.loading')}</p>
        )}

        {state.kind === 'failed' && (
          <div className="print-screen__notice print-screen__notice--error" role="alert">
            <p>
              {state.failure === 'not-found'
                ? t('print:document.notFound')
                : t('print:document.failed')}
            </p>

            {/* No retry for a patient who is not in the record — PatientOverview.tsx's
                reasoning, unchanged: the id is wrong, and asking again teaches nothing. */}
            {state.failure === 'unavailable' && (
              <button type="button" onClick={retry}>
                {t('print:document.retry')}
              </button>
            )}
          </div>
        )}
      </div>

      {state.kind === 'loaded' &&
        (documentKind === 'wristband' ? (
          <Wristband overview={state.overview} t={t} />
        ) : documentKind === 'record-extract' ? (
          <RecordExtract overview={state.overview} now={state.loadedAt} t={t} />
        ) : (
          <AdministrationRecord overview={state.overview} now={state.loadedAt} t={t} />
        ))}
    </main>
  )
}
